import { Component } from "react";
import { withTranslation } from "react-i18next";
import {
	CircularProgress,
	Paper,
	TextField,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Table,
	TableBody,
	TableHead,
	TableCell,
	TableRow,
} from "@mui/material";
// services
import Gupport from "../../services/gupport";
// types
import type { ChangeEvent } from "react";
import type { WithTranslation } from "react-i18next";
import type { UserId } from "../../types/user";
import type { CmdGetTokens, CmdSendPush, CmdDeleteToken, UserToken } from "../../types/gupport";

type Props = Readonly<WithTranslation & {
	userId: UserId;
}>;

type State = {
	loading: boolean;
	error: any;
	tokens: ReadonlyArray<UserToken>;
	selectedToken: UserToken | undefined;
	testingToken: boolean;
	buttonDisable: boolean;
	openDialog: boolean;
	openTestTokenDialog: boolean;
	openDeleteTokenDialog: boolean;
	dialogActionText: any;
	message: string | undefined;
	isDeleting: boolean;
};

class UserTokens extends Component<Props, State> {

	constructor(props: Props) {
		super(props);

		this.state = {
			loading: true,
			error: null,
			tokens: [],
			selectedToken: undefined,
			testingToken: false,
			buttonDisable: true,
			openDialog: false,
			openTestTokenDialog: false,
			openDeleteTokenDialog: false,
			dialogActionText: null,
			message: undefined,
			isDeleting: false,
		};

		this.refresh = this.refresh.bind(this);
		this.fetchTokens = this.fetchTokens.bind(this);
		this.showTestDialog = this.showTestDialog.bind(this);
		this.showDeleteDialog = this.showDeleteDialog.bind(this);
		this.handleClose = this.handleClose.bind(this);
		this.handleTextChanged = this.handleTextChanged.bind(this);
		this.testToken = this.testToken.bind(this);
		this.deleteToken = this.deleteToken.bind(this);
	}

	override componentDidMount() {
		this.fetchTokens(this.props.userId);
	}

	refresh() {
		this.fetchTokens(this.props.userId);
	}

	fetchTokens(userId) {
		const cmd = {
			action: "getTokens",
			username: userId,
		} as const satisfies CmdGetTokens;
		Gupport.send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				this.setState({
					loading: false,
					error: null,
					tokens: msg.payload.data,
				});
			} else {
				this.setState({
					loading: false,
					error: msg.payload.data,
					tokens: [],
				});
			}
		});
	}

	showTestDialog(token: UserToken) {
		this.setState({
			buttonDisable: true,
			openTestTokenDialog: true,
			selectedToken: token,
			message: undefined,
		});
	}

	showDeleteDialog(token: UserToken) {
		this.setState({
			openDeleteTokenDialog: true,
			selectedToken: token,
		});
	}

	handleClose() {
		this.setState({
			openDialog: false,
			openTestTokenDialog: false,
			openDeleteTokenDialog: false,
			selectedToken: undefined,
			message: undefined,
		});
	}

	handleTextChanged(event: ChangeEvent<HTMLInputElement>) {
		if (event.target.value.trim()) {
			this.setState({
				buttonDisable: false,
				message: event.target.value,
			});
		} else {
			this.setState({
				buttonDisable: true,
			});
		}
	}

	testToken() {
		const { t } = this.props;
		if (this.state.selectedToken && this.state.message) {
			this.setState({
				testingToken: true,
			});
			const cmd = {
				action: "sendPush",
				pusher: this.state.selectedToken.pusher,
				token: this.state.selectedToken.token,
				message: this.state.message,
			} as const satisfies CmdSendPush;
			Gupport.send(cmd, (error, msg) => {
				if (!error && msg?.payload.status === "ok") {
					this.setState({
						openTestTokenDialog: false,
						testingToken: false,
					});
				} else {
					this.setState({
						openDialog: true,
						dialogActionText: t("users.sendPush"),
						openTestTokenDialog: false,
						testingToken: false,
					});
				}
			});
		}
	}

	deleteToken() {
		const { t, userId } = this.props;
		if (this.state.selectedToken) {
			this.setState({ isDeleting: true });
			const cmd = {
				action: "deleteToken",
				token: this.state.selectedToken.token,
				username: userId,
			} as const satisfies CmdDeleteToken;
			Gupport.send(cmd, (error, msg) => {
				if (!error && msg?.payload.status === "ok") {
					this.setState({
						openDeleteTokenDialog: false,
						isDeleting: false,
					});
					this.fetchTokens(userId);
				} else {
					this.setState({
						openDialog: true,
						dialogActionText: t("users.deleteToken"),
						openDeleteTokenDialog: false,
						isDeleting: false,
					});
				}
			});
		}
	}

	override render() {
		const { t } = this.props;
		if (this.state.loading) {
			return <CircularProgress />;
		}
		if (this.state.error) {
			return <div>{this.state.error}</div>;
		}

		const columnStyle = {
			paddingRight: "5px",
			paddingLeft: "5px",
		};
		const attributeStyle = {
			whiteSpace: "normal",
			overflow: "inherit",
			wordBreak: "break-all",
			width: "90%",
			paddingRight: 0,
		};
		const isPushAvailable = Boolean(Gupport.sendPush);
		const isDeleteTokenAvailable = Boolean(Gupport.deleteToken);

		return (
			<Paper sx={{ overflow: "hidden" }}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell colSpan={3}>{t("users.agent")}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{(this.state.tokens.length === 0) ?
							<TableRow className="last-row-no-border">
								<TableCell colSpan={3}>{t("users.noTokens")}</TableCell>
							</TableRow>
							:
							this.state.tokens.map((token, index) => (
								<TableRow key={index} className="last-row-no-border">
									<TableCell sx={attributeStyle}>
										<strong>{token.agent}</strong>
										<br />
										{token.token}
									</TableCell>
									{isPushAvailable &&
										<TableCell sx={columnStyle}>
											<Button variant="contained" onClick={this.showTestDialog.bind(this, token)}>
												{t("users.test")}
											</Button>
										</TableCell>
									}
									{isDeleteTokenAvailable &&
										<TableCell sx={columnStyle}>
											<Button variant="contained" onClick={this.showDeleteDialog.bind(this, token)}>
												{t("users.delete")}
											</Button>
										</TableCell>
									}
								</TableRow>
							))
						}
					</TableBody>
				</Table>
				<Dialog
					fullWidth={true}
					open={this.state.openDialog}
					onClose={this.handleClose}
				>
					<DialogTitle>{t("users.dialog.title")}</DialogTitle>
					<DialogContent>
						<DialogContentText>
							{t("users.dialog.content", { text: this.state.dialogActionText })}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={this.handleClose}>{t("dialog.ok")}</Button>
					</DialogActions>
				</Dialog>
				<Dialog
					fullWidth={true}
					open={this.state.openTestTokenDialog}
					onClose={this.handleClose}
				>
					<DialogTitle>{t("users.dialogSend")}</DialogTitle>
					<DialogContent>
						<DialogContentText>
							{t("users.notificationMessage")}
							&nbsp;
							<TextField
								placeholder={t("users.messageHint")}
								fullWidth={true}
								onChange={this.handleTextChanged}
							/>
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button
							disabled={this.state.buttonDisable}
							loading={this.state.testingToken}
							onClick={this.testToken}
						>
							{t("users.send")}
						</Button>
						<Button color="inherit" onClick={this.handleClose}>{t("dialog.cancel")}</Button>
					</DialogActions>
				</Dialog>
				<Dialog
					fullWidth={true}
					open={this.state.openDeleteTokenDialog}
					onClose={this.handleClose}
				>
					<DialogTitle>{t("users.dialogDelete")}</DialogTitle>
					<DialogContent>
						<DialogContentText>
							{t("users.deleteConfirm")}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button color="danger" loading={this.state.isDeleting} onClick={this.deleteToken}>{t("dialog.delete")}</Button>
						<Button color="inherit" onClick={this.handleClose}>{t("dialog.cancel")}</Button>
					</DialogActions>
				</Dialog>
			</Paper>
		);
	}

}

export default withTranslation()(UserTokens);
